import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckCircleIcon, Loader2Icon, AlertTriangleIcon } from 'lucide-react'
import { fetchReadyToBillItems, type ReadyToBillItem } from '../lib/readyToBill'
import { generateInvoicesFromReadyItems } from '../lib/createJobInvoices'
import { useDivision } from '../contexts/DivisionContext'
import {
  MobilePage,
  MobilePageHeader,
  MobileListGroup,
  MobileEmptyState,
  mobile,
} from '../components/mobile'

export function ReadyToBill() {
  const navigate = useNavigate()
  const { currentDivision } = useDivision()
  const [items, setItems] = useState<ReadyToBillItem[]>([])
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)

  const load = async () => {
    setLoading(true)
    try {
      const data = await fetchReadyToBillItems(currentDivision)
      setItems(data)
      setSelected(new Set(data.filter((i) => !i.blocked).map((i) => i.key)))
    } finally {
      setLoading(false)
    }
  }

  React.useEffect(() => {
    load()
  }, [currentDivision])

  const toggle = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const totalSelected = items
    .filter((i) => selected.has(i.key))
    .reduce((s, i) => s + i.amount, 0)

  const handleGenerate = async () => {
    const toCreate = items.filter((i) => selected.has(i.key))
    if (!toCreate.length) {
      alert('Select at least one item')
      return
    }
    setGenerating(true)
    try {
      const { created, errors } = await generateInvoicesFromReadyItems(toCreate)
      if (errors.length) alert(`Created ${created} invoice(s). Errors:\n${errors.join('\n')}`)
      else alert(`Created ${created} invoice(s) successfully.`)
      await load()
      navigate('/billing')
    } catch (e) {
      alert('Failed: ' + (e as Error).message)
    } finally {
      setGenerating(false)
    }
  }

  const byCustomer = items.reduce<Record<string, ReadyToBillItem[]>>((acc, item) => {
    const k = item.customer_name
    acc[k] = acc[k] || []
    acc[k].push(item)
    return acc
  }, {})
  const blockedCount = items.filter((i) => i.blocked).length

  return (
    <MobilePage>
      <MobilePageHeader
        title="Ready to Bill"
        subtitle="Review contract periods, completed jobs, and pending milestones before invoicing."
        onBack={() => navigate('/billing')}
        backLabel="Back to Billing"
        actions={
          <button
            type="button"
            onClick={handleGenerate}
            disabled={generating || selected.size === 0}
            className={mobile.successBtn}
          >
            {generating ? <Loader2Icon className="w-4 h-4 animate-spin" /> : <CheckCircleIcon className="w-4 h-4" />}
            <span className="sm:hidden">${totalSelected.toFixed(0)}</span>
            <span className="hidden sm:inline">
              Generate {selected.size} — ${totalSelected.toFixed(2)}
            </span>
          </button>
        }
      />

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2Icon className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : items.length === 0 ? (
        <MobileEmptyState title="Nothing ready to bill right now." />
      ) : (
        <div className="space-y-4">
          {blockedCount > 0 && (
            <div className="flex items-center gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-sm">
              <AlertTriangleIcon className="w-4 h-4 shrink-0" />
              {blockedCount} item{blockedCount !== 1 ? 's' : ''} need{blockedCount === 1 ? 's' : ''} attention before they can be billed.
            </div>
          )}
          {Object.entries(byCustomer).map(([customer, group]) => (
            <MobileListGroup key={customer} label={customer}>
              {group.map((item) => (
                <label
                  key={item.key}
                  className={`${mobile.row} flex items-center gap-3 cursor-pointer ${
                    item.blocked ? 'bg-amber-50/60' : ''
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(item.key)}
                    disabled={item.blocked}
                    onChange={() => toggle(item.key)}
                    className="rounded border-gray-300 disabled:opacity-50 w-5 h-5"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{item.label}</p>
                    {item.blocked ? (
                      <p className="text-xs text-amber-700 flex items-center gap-1 mt-0.5">
                        <AlertTriangleIcon className="w-3 h-3 shrink-0" />
                        {item.blocked_reason || 'Needs review before billing.'}
                      </p>
                    ) : (
                      <p className="text-xs text-gray-500">
                        {item.scenario || item.kind.replace(/_/g, ' ')}
                      </p>
                    )}
                  </div>
                  <span className={`text-sm font-semibold flex-shrink-0 ${item.blocked ? 'text-amber-700' : 'text-gray-900'}`}>
                    ${item.amount.toFixed(2)}
                  </span>
                </label>
              ))}
            </MobileListGroup>
          ))}
        </div>
      )}
    </MobilePage>
  )
}
